import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignInputAssignDesignOutputDialog from '../dialogs/DesignInputAssignDesignOutputDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindProductDesignOutputsByDesignInputId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductDesignOutput,
  BaseProductDesignInputCardProps
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const DesignInputDesignOutputReferenceCard: React.FC<BaseProductDesignInputCardProps> =
  (props: BaseProductDesignInputCardProps) => {
    const {
      productId,
      productDesignInput,
      productDesignInputVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productDesignOutputList,
      isLoadingProductDesignOutputs,
      loadingProductDesignOutputsError,
      findProductDesignOutputs
    } = useFindProductDesignOutputsByDesignInputId(
      productId,
      productDesignInput.id
    );
    const { isFieldDisabled } = useTaskEntityFields(productDesignInput.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.DesignOutput
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductDesignOutputsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productDesignInputVersion)
        params.version = productDesignInputVersion.version;
      findProductDesignOutputs(params);
    }, [
      findProductDesignOutputs,
      order,
      orderDirection,
      productDesignInputVersion
    ]);

    useEffect(() => {
      findProductDesignOutputsWithParams();
    }, [findProductDesignOutputsWithParams]);

    useEffect(() => {
      if (loadingProductDesignOutputsError) {
        onError?.(loadingProductDesignOutputsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductDesignOutputsError]);
    const disabled =
      Boolean(productDesignInputVersion) ||
      isFieldDisabled('assignDesignOutput');
    return (
      <Card>
        <CardTitle>
          <AssignmentIcon />
          {getProductEntityName('designOutput')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignOutput.id'}
                    direction={getSortDirection('productDesignOutput.id')}
                    onClick={() => handleSort('productDesignOutput.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignOutput.title'}
                    direction={getSortDirection('productDesignOutput.title')}
                    onClick={() => handleSort('productDesignOutput.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductDesignOutputs && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productDesignOutputList?.map(
                (designOutput: ProductDesignOutput) => (
                  <TableRow
                    hover
                    key={designOutput.id}
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/design-outputs/${designOutput.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + designOutput.id}</TableCell>
                    <WrappedTableCell>{designOutput.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={designOutput.task} />
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      {!disabled && (
                        <CloseIcon
                          onClick={event => {
                            event.stopPropagation();
                            setItemToUnlink(designOutput.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )
              )}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign {getProductEntityName('designOutput')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <DesignInputAssignDesignOutputDialog
            open={openDialog}
            productId={productId}
            designInputId={productDesignInput.id}
            selectedItems={productDesignOutputList}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductDesignOutputsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productDesignInput.id}
          from={ProductBoardColumnEntity.DesignInput}
          to={ProductBoardColumnEntity.DesignOutput}
          unlinkItemId={itemToUnlink}
          selectedItems={productDesignOutputList}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            findProductDesignOutputsWithParams();
            onUpdate?.();
            setItemToUnlink(undefined);
          }}
        />
      </Card>
    );
  };

export default DesignInputDesignOutputReferenceCard;
